import { t } from 'i18next';
import {
  HashIcon,
  MailIcon,
  MessageSquareIcon,
  PhoneIcon,
  GoalIcon,
  SmartphoneIcon,
} from 'lucide-react';

import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Campaign } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';

interface ChannelCellProps {
  campaign: Campaign;
}

/**
 * Maps integration types to their respective channel icons
 */
export const getChannelIcon = (integrationType: IntegrationType | string | null, size = 16) => {
  if (!integrationType) {
    return <HashIcon size={size} className="text-muted-foreground" />;
  }

  // First try to get the integration-specific icon
  const IntegrationIcon =
    integrationIconByType[integrationType as keyof typeof integrationIconByType];
  if (IntegrationIcon) {
    return <IntegrationIcon size={size} />;
  }

  // Fallback to generic channel icons based on type
  switch (integrationType.toLowerCase()) {
    case 'whatsapp':
      return <MessageSquareIcon size={size} className="text-muted-foreground" />;
    case 'infobip-sms':
    case 'infobip-rcs':
      return <SmartphoneIcon size={size} className="text-muted-foreground" />;
    case 'email':
      return <MailIcon size={size} className="text-muted-foreground" />;
    case 'twilio-voice':
    case 'telnyx-voice':
      return <PhoneIcon size={size} className="text-muted-foreground" />;
    default:
      return <GoalIcon size={size} className="text-muted-foreground" />;
  }
};

/**
 * Cell component that displays one icon per unique channel type for a campaign.
 */
export const ChannelCell = ({ campaign }: ChannelCellProps) => {
  const senders = campaign.senders;

  if (!senders?.length) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }

  const uniqueTypes = [
    ...new Set(senders.map((s) => s.integration_type).filter((t): t is string => !!t)),
  ];

  if (uniqueTypes.length === 0) {
    return <span className="text-sm text-muted-foreground">-</span>;
  }

  return (
    <div className="flex items-center gap-1.5">
      {uniqueTypes.map((type) => (
        <InfoTooltip key={type} content={t(getChannelKey(type as IntegrationType))} side="top">
          <span>{getChannelIcon(type)}</span>
        </InfoTooltip>
      ))}
    </div>
  );
};
